import React, { useState, useEffect } from 'react';
import { Calculator, Check, ArrowRight, Clock, ShieldCheck, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import { useLanguage } from '../context/LanguageContext';

interface BaseTier {
  id: string;
  name: string;
  nameUrdu: string;
  price: number;
  days: number;
  description: string;
  descriptionUrdu: string;
  formType: string;
}

interface AddonFeature {
  id: string;
  name: string;
  nameUrdu: string;
  price: number;
  days: number;
  description: string;
  descriptionUrdu: string;
}

const BASE_TIERS: BaseTier[] = [
  {
    id: 'wordpress',
    name: 'WordPress Custom Web Portal',
    nameUrdu: 'ورڈپریس کسٹم ویب پورٹل',
    price: 180,
    days: 4,
    description: 'Custom WordPress site built with Elementor Pro, responsive mobile layout, custom themes, and fast loading.',
    descriptionUrdu: 'ایلیمنٹر پرو، ریسپانسیو موبائل لے آؤٹ، کسٹم تھیمز اور فاسٹ لوڈنگ کے ساتھ مکمل ورڈپریس ویب سائٹ۔',
    formType: 'wordpress'
  },
  {
    id: 'ai-coded',
    name: 'AI + Custom Coded Website',
    nameUrdu: 'اے آئی اور کسٹم کوڈڈ ویب سائٹ',
    price: 240,
    days: 5,
    description: 'Lightning-fast website leveraging cutting-edge AI generation tools combined with clean hand-crafted HTML/CSS/JS/React code.',
    descriptionUrdu: 'جدید اے آئی اور کلین ہاتھ سے لکھے گئے HTML/CSS/JS/React کوڈ سے تیار کردہ انتہائی تیز رفتار ویب سائٹ۔',
    formType: 'ai-coded-web'
  },
  {
    id: 'ai-video',
    name: 'AI Video Editing & Motion',
    nameUrdu: 'اے آئی ویڈیو ایڈیٹنگ اور موشن',
    price: 120,
    days: 3,
    description: 'Dynamic AI video editing, CapCut effects, Instagram Reels, TikToks, Shorts, AI voiceover, and synced subtitles.',
    descriptionUrdu: 'ڈائنامک اے آئی ویڈیو ایڈیٹنگ، کیپ کٹ ایفیکٹس، انسٹاگرام ریلز، شارٹس، اے آئی وائس اوور اور سب ٹائٹلز۔',
    formType: 'ai-video'
  },
  {
    id: 'graphic',
    name: 'Logo & Graphic Design Suite',
    nameUrdu: 'لوگو اور گرافک ڈیزائن سویٹ',
    price: 90,
    days: 2,
    description: 'Standout brand logos, custom vector graphics, Canva templates, and marketing materials in Adobe Illustrator/Photoshop.',
    descriptionUrdu: 'نمایاں برانڈ لوگوز، کسٹم ویکٹر گرافکس، کینوا ٹیمپلیٹس اور ایڈوب السٹریٹر/فوٹوشاپ میں مارکیٹنگ گرافکس۔',
    formType: 'graphic-design'
  }
];

const ADDON_FEATURES: AddonFeature[] = [
  {
    id: 'ecommerce',
    name: 'WooCommerce & E-Commerce Store',
    nameUrdu: 'وو کامرس اور آن لائن اسٹور',
    price: 120,
    days: 3,
    description: 'Complete online store setup with product upload, payment gateway, cart, and order management.',
    descriptionUrdu: 'پروڈکٹس اپ لوڈ، پیمنٹ گیٹ وے، شاپنگ کارٹ اور آرڈر مینجمنٹ کے ساتھ مکمل آن لائن اسٹور۔'
  },
  {
    id: 'custom-code',
    name: 'Custom PHP & Code Tweaks',
    nameUrdu: 'کسٹم پی ایچ پی اور کوڈ ٹویکس',
    price: 70,
    days: 2,
    description: 'Bespoke code snippets, custom CSS styling, and tailored functionality overrides.',
    descriptionUrdu: 'کسٹم کوڈ اسنیپٹس، خصوصی سی ایس ایس اسٹائلنگ اور کسٹم فنکشنلٹی۔'
  },
  {
    id: 'ai-voice-captions',
    name: 'AI Voiceover & Auto-Captions',
    nameUrdu: 'اے آئی وائس اوور اور کیپشنز',
    price: 60,
    days: 1,
    description: 'AI narration in natural accents, auto-synced subtitles, and dynamic video transitions.',
    descriptionUrdu: 'قدرتی لہجوں میں اے آئی آواز، آٹو سنک سب ٹائٹلز اور ڈائنامک ویڈیو ٹرانزیشنز۔'
  },
  {
    id: 'seo-speed',
    name: 'SEO & Speed Optimization Pack',
    nameUrdu: 'ایس ای او اور اسپیڈ پیک',
    price: 75,
    days: 2,
    description: 'Under-2-second load times, caching, image compression, and Google search engine indexing.',
    descriptionUrdu: '2 سیکنڈ سے کم لوڈ ٹائم، کیشنگ، امیج کمپریشن اور گوگل سرچ انڈیکسنگ۔'
  },
  {
    id: 'ai-bot',
    name: 'AI Assistant / Chatbot Integration',
    nameUrdu: 'اے آئی اسسٹنٹ / چیٹ بوٹ',
    price: 150,
    days: 3,
    description: 'Smart Google Gemini AI chatbot embedded on your site for automated visitor support.',
    descriptionUrdu: 'خودکار کسٹمر سپورٹ کے لیے آپ کی ویب سائٹ پر گوگل جیمنائی اے آئی چیٹ بوٹ انٹیگریشن۔'
  },
  {
    id: 'brand-kit',
    name: 'Social Media & Brand Kit',
    nameUrdu: 'سوشل میڈیا اور برانڈ کٹ',
    price: 65,
    days: 2,
    description: 'Custom banners, YouTube thumbnails, ad graphics, and brand style guidelines.',
    descriptionUrdu: 'کسٹم بینرز، یوٹیوب تھمب نیلز، ایڈورٹائزمنٹ گرافکس اور برانڈ اسٹائل گائیڈ۔'
  }
];

const CostCalculator: React.FC = () => {
  const { t, isUrdu } = useLanguage();
  const [selectedTier, setSelectedTier] = useState<string>('wordpress');
  const [selectedAddons, setSelectedAddons] = useState<string[]>(['seo-speed', 'custom-code']);
  const [urgency, setUrgency] = useState<string>('standard'); // 'relaxed', 'standard', 'express'
  const [animatedCost, setAnimatedCost] = useState<number>(0);
  const [mobileStep, setMobileStep] = useState<number>(1); // 1: Base, 2: Addons, 3: Review & Summary

  // Find current base tier details
  const activeTier = BASE_TIERS.find(t => t.id === selectedTier) || BASE_TIERS[0];

  // Calculate totals
  const basePrice = activeTier.price;
  const baseDays = activeTier.days;

  const addonsPrice = ADDON_FEATURES
    .filter(addon => selectedAddons.includes(addon.id))
    .reduce((sum, addon) => sum + addon.price, 0);

  const addonsDays = ADDON_FEATURES
    .filter(addon => selectedAddons.includes(addon.id))
    .reduce((sum, addon) => sum + addon.days, 0);

  let subtotal = basePrice + addonsPrice;
  let totalDays = baseDays + addonsDays;

  // Urgency modifier
  let urgencyMultiplier = 1.0;
  let urgencyDaysMultiplier = 1.0;
  if (urgency === 'relaxed') {
    urgencyMultiplier = 0.9;
    urgencyDaysMultiplier = 1.3;
  } else if (urgency === 'express') {
    urgencyMultiplier = 1.35;
    urgencyDaysMultiplier = 0.6;
  }

  const finalCost = Math.round(subtotal * urgencyMultiplier);
  const finalDays = Math.max(2, Math.round(totalDays * urgencyDaysMultiplier));

  // Pricing count-up animation
  useEffect(() => {
    let start = animatedCost;
    const end = finalCost;
    if (start === end) return;

    const duration = 600;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = progress * (2 - progress);
      const nextValue = Math.round(start + (end - start) * easeProgress);

      setAnimatedCost(nextValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [finalCost]);

  const toggleAddon = (addonId: string) => {
    setSelectedAddons(prev =>
      prev.includes(addonId)
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const handleApplySpecs = () => {
    const selectedAddonNames = ADDON_FEATURES
      .filter(addon => selectedAddons.includes(addon.id))
      .map(addon => isUrdu ? addon.nameUrdu : addon.name);

    let urgencyLabel = isUrdu ? "معیاری رفتار" : "Standard Speed";
    if (urgency === 'relaxed') urgencyLabel = isUrdu ? "آرام دہ رفتار (رعایت)" : "Relaxed Speed (Flexible)";
    if (urgency === 'express') urgencyLabel = isUrdu ? "فاسٹ ایکسپریس رفتار" : "Express Speed (Fastest)";

    const messageContent = `Hi Taibe, I evaluated a project scope using your interactive Estimator:
- Tier: ${isUrdu ? activeTier.nameUrdu : activeTier.name}
- Selected Modules: ${selectedAddonNames.length > 0 ? selectedAddonNames.join(', ') : 'None'}
- Delivery Speed: ${urgencyLabel}
- Approximate Budget: ${finalCost}
- Approximate Timeline: ${finalDays} ${isUrdu ? 'دن' : 'Days'}

I would love to discuss this project with you!`;

    confetti({
      particleCount: 180,
      spread: 80,
      origin: { y: 0.8 },
      colors: ['#6366f1', '#3b82f6', '#8b5cf6', '#ec4899']
    });

    const customEvent = new CustomEvent('populateForm', {
      detail: {
        projectType: activeTier.formType,
        message: messageContent
      }
    });
    window.dispatchEvent(customEvent);

    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="estimator" 
      aria-label="Interactive Project Cost & Timeline Estimator"
      className="py-12 sm:py-24 bg-slate-50/70 relative overflow-hidden border-y border-slate-200/80 w-full"
    >
      {/* Background subtle radial gradient */}
      <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-indigo-100/40 blur-[150px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-blue-100/30 blur-[150px] rounded-full pointer-events-none"></div>

      <div className="w-full px-4 sm:px-12 lg:px-16 xl:px-24 relative z-10">
        
        {/* Compact Prominent Header */}
        <div className="text-center mb-8 sm:mb-16 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-indigo-50 border border-indigo-200/80 rounded-full mb-3 shadow-xs">
            <Sparkles size={14} className="text-indigo-600 animate-spin-slow" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-700">
              {t.calculator.badge}
            </span>
          </div>
          <h2 className="text-2xl sm:text-5xl font-display font-extrabold text-slate-900 tracking-tight">
            {t.calculator.title}
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm max-w-xl mx-auto mt-2 sm:mt-3 leading-relaxed font-normal">
            {t.calculator.subtitle}
          </p>
        </div>

        {/* 1. MOBILE RESPONSIVE WIZARD (Visible ONLY on mobile < md) */}
        <div className="block md:hidden w-full max-w-lg mx-auto">
          {/* Step Progress Bar & Tab Selectors */}
          <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-xs mb-4 flex items-center justify-between gap-1">
            {[
              { step: 1, label: isUrdu ? '1. سروس' : '1. Service' },
              { step: 2, label: isUrdu ? '2. فیچرز' : '2. Add-ons' },
              { step: 3, label: isUrdu ? '3. کوٹیشن' : '3. Quote' }
            ].map((tab) => (
              <button
                key={tab.step}
                onClick={() => setMobileStep(tab.step)}
                className={`flex-1 py-2 px-1 text-center rounded-xl text-xs font-bold transition-all duration-200 flex items-center justify-center gap-1.5 ${
                  mobileStep === tab.step
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : mobileStep > tab.step
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <span>{tab.label}</span>
                {mobileStep > tab.step && <Check size={12} strokeWidth={3} className="text-indigo-600" />}
              </button>
            ))}
          </div>

          {/* Step Content Container */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm mb-4">
            
            {/* Step 1: Base Tier Selection */}
            {mobileStep === 1 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-xs font-black uppercase text-slate-800 tracking-wider">
                    {t.calculator.step1Title}
                  </span>
                  <span className="text-[10px] text-indigo-600 font-bold bg-indigo-50 px-2 py-0.5 rounded-full">
                    {isUrdu ? 'مرحلہ 1 از 3' : 'Step 1 of 3'}
                  </span>
                </div>
                <div className="space-y-2.5">
                  {BASE_TIERS.map((tier) => (
                    <button
                      key={tier.id}
                      onClick={() => {
                        setSelectedTier(tier.id);
                        if (tier.id === 'graphic' || tier.id === 'ai-video') {
                          if (tier.id === 'ai-video') {
                            setSelectedAddons(['ai-voice-captions']);
                          } else {
                            setSelectedAddons([]);
                          }
                        } else if (selectedAddons.length === 0) {
                          setSelectedAddons(['seo-speed', 'custom-code']);
                        }
                      }}
                      className={`w-full p-3 rounded-xl text-left border transition-all duration-200 flex items-center justify-between gap-3 ${
                        selectedTier === tier.id
                          ? 'bg-indigo-50/90 border-indigo-600 shadow-xs ring-1 ring-indigo-500'
                          : 'bg-slate-50/60 border-slate-200'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-900 truncate">
                            {isUrdu ? tier.nameUrdu : tier.name}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-500 line-clamp-1 mt-0.5">
                          {isUrdu ? tier.descriptionUrdu : tier.description}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-xs font-mono font-black ${selectedTier === tier.id ? 'text-indigo-700' : 'text-slate-700'}`}>
                          ${tier.price}+
                        </span>
                        <div className="text-[9px] text-slate-400 font-mono">~{tier.days}d</div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="pt-2 flex justify-end">
                  <button
                    onClick={() => setMobileStep(2)}
                    className="w-full py-3 bg-indigo-600 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-sm active:scale-98 cursor-pointer"
                  >
                    <span>{isUrdu ? 'اضافی فیچرز کی طرف بڑھیں' : 'Continue to Add-ons'}</span>
                    <ArrowRight size={14} className={isUrdu ? 'rotate-180' : ''} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Add-on Modules */}
            {mobileStep === 2 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div>
                    <span className="text-xs font-black uppercase text-slate-800 tracking-wider">
                      {t.calculator.step2Title}
                    </span>
                    <p className="text-[10px] text-slate-500">
                      {isUrdu ? 'اپنی مرضی کی خصوصیات منتخب کریں' : 'Pick features to include'}
                    </p>
                  </div>
                  <span className="text-[10px] text-indigo-600 font-bold bg-indigo-50 px-2 py-0.5 rounded-full">
                    {isUrdu ? 'مرحلہ 2 از 3' : 'Step 2 of 3'}
                  </span>
                </div>
                
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {ADDON_FEATURES.map((addon) => {
                    const isSelected = selectedAddons.includes(addon.id);
                    return (
                      <button
                        key={addon.id}
                        onClick={() => toggleAddon(addon.id)}
                        className={`w-full p-2.5 rounded-xl text-left border transition-all duration-200 flex items-center justify-between gap-2.5 cursor-pointer ${
                          isSelected
                            ? 'bg-indigo-50/90 border-indigo-600 ring-1 ring-indigo-500'
                            : 'bg-slate-50/60 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className={`w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 ${
                            isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 bg-white'
                          }`}>
                            {isSelected && <Check size={10} strokeWidth={3.5} />}
                          </div>
                          <span className="text-xs font-bold text-slate-800 truncate">
                            {isUrdu ? addon.nameUrdu : addon.name}
                          </span>
                        </div>
                        <span className="text-[11px] font-mono font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded flex-shrink-0">
                          +${addon.price}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="pt-2 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMobileStep(1)}
                    className="py-2.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-bold cursor-pointer"
                  >
                    {isUrdu ? 'پیچھے' : 'Back'}
                  </button>
                  <button
                    onClick={() => setMobileStep(3)}
                    className="py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                  >
                    <span>{isUrdu ? 'کوٹیشن دیکھیں' : 'View Quote'}</span>
                    <ArrowRight size={14} className={isUrdu ? 'rotate-180' : ''} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Speed & Summary Card */}
            {mobileStep === 3 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-xs font-black uppercase text-slate-800 tracking-wider">
                    {t.calculator.step3Title}
                  </span>
                  <span className="text-[10px] text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded-full">
                    {isUrdu ? 'مرحلہ 3 از 3' : 'Step 3 of 3'}
                  </span>
                </div>

                {/* Urgency selector */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1.5">
                    {isUrdu ? 'ڈیلیوری کی رفتار' : 'Delivery Pacing'}
                  </label>
                  <div className="grid grid-cols-3 gap-1.5 bg-slate-100 p-1.5 rounded-xl">
                    {[
                      { id: 'relaxed', title: isUrdu ? 'آرام دہ' : 'Relaxed', tag: '-10%' },
                      { id: 'standard', title: isUrdu ? 'معیاری' : 'Standard', tag: 'Normal' },
                      { id: 'express', title: isUrdu ? 'فاسٹ' : 'Express', tag: '+35%' }
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setUrgency(opt.id)}
                        className={`py-2 rounded-lg text-center text-xs font-bold transition-all cursor-pointer ${
                          urgency === opt.id
                            ? 'bg-white text-indigo-700 shadow-xs border border-indigo-200'
                            : 'text-slate-600'
                        }`}
                      >
                        <div>{opt.title}</div>
                        <div className="text-[9px] font-mono text-slate-400 font-normal">{opt.tag}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Spec Recap */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{isUrdu ? 'سروس:' : 'Service:'}</span>
                    <span className="font-bold text-slate-900 truncate max-w-[170px]">
                      {isUrdu ? activeTier.nameUrdu : activeTier.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{isUrdu ? 'ماڈیولز:' : 'Modules:'}</span>
                    <span className="font-bold text-indigo-600">
                      {selectedAddons.length} {isUrdu ? 'منتخب کردہ' : 'Selected'}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200/80 pt-1.5">
                    <span className="text-slate-500">{isUrdu ? 'تخمینی وقت:' : 'Est. Time:'}</span>
                    <span className="font-bold text-slate-900 font-mono">
                      ~{finalDays} {isUrdu ? 'کاروباری دن' : 'Business Days'}
                    </span>
                  </div>
                </div>

                {/* Total Price & CTA */}
                <div className="bg-slate-900 text-white rounded-xl p-4 text-center space-y-3">
                  <div>
                    <span className="text-[10px] uppercase font-mono tracking-wider text-indigo-300">
                      {isUrdu ? 'کل تخمینی لاگت' : 'Total Estimate'}
                    </span>
                    <div className="text-3xl font-black font-mono text-white mt-0.5">${animatedCost} <span className="text-xs font-normal text-slate-400">USD</span></div>
                  </div>

                  <button
                    onClick={handleApplySpecs}
                    className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md active:scale-98 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <span>{t.calculator.ctaButton}</span>
                    <ArrowRight size={14} className={isUrdu ? 'rotate-180' : ''} />
                  </button>
                </div>

                <div className="pt-1 text-center">
                  <button
                    onClick={() => setMobileStep(2)}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-800 underline cursor-pointer"
                  >
                    {isUrdu ? 'اضافی فیچرز میں تبدیلی کریں' : 'Edit Add-on Modules'}
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* Mobile Persistent Summary Bar at Bottom of section (Visible on Steps 1 & 2) */}
          {mobileStep < 3 && (
            <div className="bg-slate-900 text-white rounded-xl p-3 flex items-center justify-between shadow-lg">
              <div>
                <span className="text-[10px] text-indigo-300 font-mono uppercase block">
                  {isUrdu ? 'کل لاگت' : 'Live Total'}
                </span>
                <span className="text-lg font-black font-mono">${animatedCost} <span className="text-[10px] font-normal text-slate-400">(~{finalDays}d)</span></span>
              </div>
              <button
                onClick={() => setMobileStep(3)}
                className="px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                <span>{isUrdu ? 'مکمل کوٹیشن دیکھیں' : 'View Full Quote'}</span>
                <ArrowRight size={13} className={isUrdu ? 'rotate-180' : ''} />
              </button>
            </div>
          )}
        </div>

        {/* 2. DESKTOP VIEW: Full 2-Column Grid Layout (Visible on md and above) */}
        <div className="hidden md:grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Interactive Settings */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-6 sm:space-y-8">
            
            {/* Step 1: Base Tier Selection */}
            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-7 shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-1">
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-indigo-600 text-white text-xs font-mono font-bold">1</span>
                  <h3 className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-slate-900">
                    {t.calculator.step1Title}
                  </h3>
                </div>
                <span className="text-[10px] text-slate-500 font-medium ml-8 sm:ml-0">
                  {isUrdu ? 'کوئی 1 بنیادی سروس منتخب کریں' : 'Choose 1 Core Base'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4 gap-3.5">
                {BASE_TIERS.map((tier) => (
                  <button
                    key={tier.id}
                    onClick={() => {
                      setSelectedTier(tier.id);
                      if (tier.id === 'graphic' || tier.id === 'ai-video') {
                        if (tier.id === 'ai-video') {
                          setSelectedAddons(['ai-voice-captions']);
                        } else {
                          setSelectedAddons([]);
                        }
                      } else if (selectedAddons.length === 0) {
                        setSelectedAddons(['seo-speed', 'custom-code']);
                      }
                    }}
                    className={`p-3.5 sm:p-4 rounded-xl text-left border transition-all duration-200 relative group flex flex-col justify-between cursor-pointer ${
                      selectedTier === tier.id
                        ? 'bg-indigo-50/80 border-indigo-600 shadow-md ring-2 ring-indigo-600/20'
                        : 'bg-slate-50/50 border-slate-200 hover:border-indigo-300 hover:bg-white'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <span className="text-xs font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                          {isUrdu ? tier.nameUrdu : tier.name}
                        </span>
                        {selectedTier === tier.id ? (
                          <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center text-white flex-shrink-0">
                            <Check size={11} strokeWidth={3} />
                          </div>
                        ) : (
                          <span className="text-xs font-mono font-bold text-slate-600 bg-slate-200/60 px-2 py-0.5 rounded-md flex-shrink-0">${tier.price}+</span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 leading-snug">
                        {isUrdu ? tier.descriptionUrdu : tier.description}
                      </p>
                    </div>
                    {selectedTier === tier.id && (
                      <div className="flex items-center justify-between border-t border-indigo-200/80 pt-2.5 mt-3">
                        <span className="text-[10px] font-mono text-indigo-700 font-bold uppercase tracking-wider">
                          {isUrdu ? 'منتخب شدہ' : 'Selected Base'}
                        </span>
                        <span className="text-xs font-mono font-black text-indigo-700">${tier.price}+</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Add-on Features */}
            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-7 shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-1">
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-indigo-600 text-white text-xs font-mono font-bold">2</span>
                  <h3 className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-slate-900">
                    {t.calculator.step2Title}
                  </h3>
                </div>
                <span className="text-[10px] text-slate-500 font-medium ml-8 sm:ml-0">
                  {isUrdu ? 'ایک سے زیادہ فیچرز منتخب کیے جا سکتے ہیں' : 'Multiple Selection Available'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                {ADDON_FEATURES.map((addon) => {
                  const isSelected = selectedAddons.includes(addon.id);
                  return (
                    <button
                      key={addon.id}
                      onClick={() => toggleAddon(addon.id)}
                      className={`p-3 sm:p-3.5 rounded-xl text-left border transition-all duration-200 flex gap-2.5 items-start cursor-pointer ${
                        isSelected
                          ? 'bg-indigo-50/80 border-indigo-500 shadow-xs ring-1 ring-indigo-500/30'
                          : 'bg-slate-50/40 border-slate-200 hover:border-indigo-200 hover:bg-white'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded mt-0.5 flex items-center justify-center border transition-all flex-shrink-0 ${
                        isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 bg-white'
                      }`}>
                        {isSelected && <Check size={10} strokeWidth={3.5} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1.5 mb-1">
                          <span className="font-bold text-xs text-slate-900 truncate">
                            {isUrdu ? addon.nameUrdu : addon.name}
                          </span>
                          <span className="text-[10px] font-mono text-indigo-600 font-bold bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100 flex-shrink-0">
                            +${addon.price}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-500 line-clamp-2 leading-tight">
                          {isUrdu ? addon.descriptionUrdu : addon.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 3: Deployment Speed */}
            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-7 shadow-sm space-y-3.5">
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-indigo-600 text-white text-xs font-mono font-bold">3</span>
                <h3 className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-slate-900">
                  {isUrdu ? 'ڈیلیوری ٹائم لائن اور رفتار' : 'Deployment Urgency & Pacing'}
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 bg-slate-100/80 p-2 rounded-xl border border-slate-200/80">
                {[
                  { id: 'relaxed', title: isUrdu ? 'آرام دہ رفتار' : 'Relaxed Pace', tag: isUrdu ? '-10% رعایت' : '-10% Discount' },
                  { id: 'standard', title: isUrdu ? 'معیاری رفتار' : 'Standard Speed', tag: isUrdu ? 'ریگولر ریٹ' : 'Regular Rate' },
                  { id: 'express', title: isUrdu ? 'فاسٹ ایکسپریس' : 'Express Urgent', tag: isUrdu ? '+35% ترجیح' : '+35% Priority' }
                ].map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setUrgency(option.id)}
                    className={`py-3 px-3 rounded-lg text-center transition-all duration-200 cursor-pointer flex items-center justify-center ${
                      urgency === option.id
                        ? option.id === 'express'
                          ? 'bg-white text-rose-700 font-bold shadow-sm border border-rose-200 ring-2 ring-rose-500/20'
                          : option.id === 'relaxed'
                          ? 'bg-white text-emerald-700 font-bold shadow-sm border border-emerald-200 ring-2 ring-emerald-500/20'
                          : 'bg-white text-indigo-700 font-bold shadow-sm border border-indigo-200 ring-2 ring-indigo-500/20'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                    }`}
                  >
                    <div className="text-xs sm:text-sm font-bold flex items-center justify-center gap-2 flex-wrap sm:flex-nowrap text-center">
                      <span className="whitespace-nowrap">{option.title}</span>
                      <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${
                        urgency === option.id
                          ? option.id === 'express' ? 'bg-rose-100 text-rose-700' : option.id === 'relaxed' ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        {option.tag}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

          </div>

          {/* Right Column: Prominent Summary Card */}
          <div className="lg:col-span-5 xl:col-span-4 w-full">
            <div className="bg-white rounded-2xl border-2 border-indigo-600/30 shadow-xl p-5 sm:p-8 lg:sticky lg:top-24 space-y-5">
              
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-xs">
                    <Calculator size={20} />
                  </div>
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-900">
                      {isUrdu ? 'لائیو کوٹیشن' : 'Live Quotation'}
                    </h4>
                    <p className="text-[10px] text-slate-500">
                      {isUrdu ? 'پروجیکٹ کی تخمینی تفصیلات' : 'Estimated Project Summary'}
                    </p>
                  </div>
                </div>
                <span className="text-[9px] font-mono font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full uppercase">
                  {isUrdu ? 'فوری پیش نظارہ' : 'Instant Preview'}
                </span>
              </div>

              {/* Selected Specs List */}
              <div className="space-y-3.5 text-xs">
                <div className="flex justify-between items-center py-1">
                  <span className="text-slate-500 font-medium">{isUrdu ? 'بنیادی سروس:' : 'Architecture:'}</span>
                  <span className="font-bold text-slate-900 text-right max-w-[210px] truncate bg-slate-50 border border-slate-200/80 px-2.5 py-1 rounded-lg">
                    {isUrdu ? activeTier.nameUrdu : activeTier.name}
                  </span>
                </div>

                {selectedAddons.length > 0 && (
                  <div className="py-1">
                    <div className="text-slate-500 font-medium mb-2">
                      {isUrdu ? `منتخب ماڈیولز (${selectedAddons.length}):` : `Selected Modules (${selectedAddons.length}):`}
                    </div>
                    <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
                      {ADDON_FEATURES.filter(addon => selectedAddons.includes(addon.id)).map(addon => (
                        <span key={addon.id} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-[10px] font-medium border border-indigo-100/90 shadow-2xs">
                          + {isUrdu ? addon.nameUrdu : addon.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center py-1 border-t border-slate-100">
                  <span className="text-slate-500 font-medium">{isUrdu ? 'رفتار:' : 'Pacing:'}</span>
                  <span className={`font-bold capitalize text-xs bg-slate-50 border border-slate-200/80 px-2.5 py-1 rounded-lg ${
                    urgency === 'express' ? 'text-rose-600' : urgency === 'relaxed' ? 'text-emerald-600' : 'text-indigo-600'
                  }`}>
                    {urgency === 'express' ? (isUrdu ? 'فاسٹ ایکسپریس' : 'Express Speed') : urgency === 'relaxed' ? (isUrdu ? 'آرام دہ رفتار' : 'Relaxed Speed') : (isUrdu ? 'معیاری رفتار' : 'Standard Speed')}
                  </span>
                </div>
              </div>

              {/* Timeline pill */}
              <div className="bg-slate-50 rounded-xl p-3.5 flex items-center justify-between border border-slate-200/90">
                <div className="flex items-center gap-2 text-slate-700 text-xs font-medium">
                  <Clock size={16} className="text-indigo-600" />
                  <span>{t.calculator.timeline}:</span>
                </div>
                <span className="font-mono text-sm font-black text-slate-900 bg-white border border-slate-200 px-3 py-1 rounded-lg shadow-2xs">
                  ~{finalDays} {isUrdu ? 'کاروباری دن' : 'Business Days'}
                </span>
              </div>

              {/* Total Price Display */}
              <div className="bg-slate-900 text-white rounded-2xl p-6 border border-slate-800 space-y-5">
                <div className="flex items-baseline justify-between">
                  <div>
                    <span className="block text-[9px] font-mono font-bold uppercase tracking-wider text-indigo-300">
                      {isUrdu ? 'کل تخمینی لاگت' : 'Total Investment'}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {isUrdu ? 'ابتدائی تخمینہ' : 'Non-binding estimate'}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-3xl sm:text-4xl font-black text-white">
                      ${animatedCost}
                    </span>
                    <span className="block text-[9px] font-mono text-slate-400 uppercase tracking-wider">USD</span>
                  </div>
                </div>

                <div className="text-[10px] text-slate-300 leading-snug flex gap-2 pt-2 border-t border-slate-800">
                  <ShieldCheck size={15} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>
                    {isUrdu ? 'مفت مشاورت شامل ہے۔ پروجیکٹ شروع کرنے سے پہلے حتمی تفصیلات طے کی جائیں گی۔' : 'Free consultation included. Details will be confirmed before locking in.'}
                  </span>
                </div>

                <button
                  onClick={handleApplySpecs}
                  className="w-full relative group overflow-hidden bg-indigo-600 hover:bg-indigo-500 text-white font-black text-[11px] uppercase tracking-[0.25em] py-4 rounded-xl transition-all duration-200 shadow-md active:scale-98 flex items-center justify-center gap-2.5 cursor-pointer"
                >
                  <span>{t.calculator.ctaButton}</span>
                  <ArrowRight size={15} className={`transition-transform ${isUrdu ? 'group-hover:-translate-x-1 rotate-180' : 'group-hover:translate-x-1'}`} />
                </button>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default CostCalculator;
